import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import { useAuth, getPostAuthDestination, normalizeAuthRedirect } from "@/context/AuthContext";
import { formatApiError, api } from "@/lib/api";
import { auth } from "@/lib/firebase";
import {
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  sendPasswordResetEmail,
} from "firebase/auth";
import { toast } from "sonner";

function getLoginErrorMessage(t, err) {
  const code = err?.code;
  const message = err?.message || "";
  const detail = formatApiError(err?.response?.data?.detail);

  if (
    code === "auth/invalid-credential" ||
    code === "auth/invalid-email" ||
    code === "auth/wrong-password" ||
    code === "auth/user-not-found" ||
    message.includes("INVALID_LOGIN_CREDENTIALS")
  ) {
    return t("common.login_invalid_credentials");
  }
  if (code === "auth/popup-closed-by-user") {
    return t("auth.popup_closed");
  }
  if (code === "auth/too-many-requests") {
    return t("auth.too_many_requests");
  }
  return detail || message || t("common.login_failed");
}

export default function Login() {
  const { t } = useTranslation();
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = normalizeAuthRedirect(params.get("redirect"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [socialProvider, setSocialProvider] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const isBusy = submitting || Boolean(socialProvider) || resettingPassword;
  const registerLink = redirect ? `/register?redirect=${encodeURIComponent(redirect)}` : "/register";
  const hostRegisterLink = redirect ? `/register/host?redirect=${encodeURIComponent(redirect)}` : "/register/host";

  useEffect(() => {
    const authError = params.get("error");
    if (!authError) return;
    if (authError === "oauth") {
      setError(t("auth.oauth_failed"));
      return;
    }
    setError(authError);
  }, [params, t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMessage("");
    setSubmitting(true);
    try {
      const user = await login(email, password);
      toast.success(t("common.welcome_back"));
      navigate(getPostAuthDestination(user, redirect), { replace: true });
    } catch (err) {
      setError(getLoginErrorMessage(t, err));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordReset = async () => {
    const nextEmail = email.trim();
    setError("");
    setInfoMessage("");

    if (!nextEmail) {
      setError(t("auth.reset_email_required"));
      return;
    }

    setResettingPassword(true);
    try {
      await sendPasswordResetEmail(auth, nextEmail);
      setInfoMessage(t("auth.reset_password_sent", { email: nextEmail }));
    } catch (err) {
      setError(getLoginErrorMessage(t, err));
    } finally {
      setResettingPassword(false);
    }
  };

  const socialLogin = async (providerName, provider) => {
    setError("");
    setInfoMessage("");
    setSocialProvider(providerName);
    try {
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      const { data } = await api.post("/auth/oauth-callback", { id_token: idToken });
      if (data.token) localStorage.setItem("cb_token", data.token);
      if (data.user) {
        localStorage.setItem("cb_user", JSON.stringify(data.user));
        setUser(data.user);
      }
      toast.success(t("common.welcome_back"));
      navigate(getPostAuthDestination(data.user, redirect), { replace: true });
    } catch (err) {
      setError(getLoginErrorMessage(t, err));
    } finally {
      setSocialProvider("");
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1570498839593-e565b39455fc?w=800"
          className="absolute inset-0 w-full h-full object-cover grayscale-[40%]"
          alt=""
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background/40 to-transparent" />
        <div className="absolute bottom-10 left-10 max-w-md">
          <div className="label text-volt">{t("auth.welcome_back")}</div>
          <h2 className="font-heading text-5xl leading-[0.9]">{t("auth.play_hard")}</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="label-eyebrow text-volt mb-3">{t("auth.signin_eyebrow")}</div>
          <h1 className="font-heading text-5xl mb-8 tracking-tight">{t("auth.signin_title")}</h1>

          {redirect && (
            <div className="mb-5 rounded-sm border border-border bg-card/60 px-4 py-3 text-sm text-muted-foreground">
              {t("auth.redirect_notice")}
            </div>
          )}

          <div className="flex flex-col gap-3 mb-5">
            <button
              type="button"
              onClick={() => socialLogin("google", new GoogleAuthProvider())}
              data-testid="google-signin-btn"
              disabled={isBusy}
              className="w-full btn-outline rounded-sm py-3 text-xs flex items-center justify-center gap-3 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/>
                <path d="M5.84 14.1A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.44.36-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
              </svg>
              {socialProvider === "google" ? t("auth.continue_google_loading") : t("auth.continue_google")}
            </button>

            <button
              type="button"
              onClick={() => socialLogin("facebook", new FacebookAuthProvider())}
              disabled={isBusy}
              className="w-full btn-outline rounded-sm py-3 text-xs flex items-center justify-center gap-3 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              {socialProvider === "facebook" ? t("auth.continue_facebook_loading") : t("auth.continue_facebook")}
            </button>
          </div>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[10px] tracking-widest text-muted-foreground/60 uppercase">{t("auth.or_email")}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">{t("auth.email")}</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-card border border-input px-4 py-3 focus:border-volt focus:outline-none"
              />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <label className="label-eyebrow block">{t("auth.password")}</label>
                <button
                  type="button"
                  onClick={handlePasswordReset}
                  disabled={isBusy}
                  className="text-xs text-volt underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {resettingPassword ? t("auth.resetting_password") : t("auth.forgot_password")}
                </button>
              </div>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-card border border-input px-4 py-3 pe-12 focus:border-volt focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute inset-y-0 end-0 flex w-12 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={showPassword ? t("auth.hide_password") : t("auth.show_password")}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {infoMessage && (
              <div className="rounded-sm border border-volt/30 bg-volt/10 px-4 py-3 text-sm text-foreground">
                {infoMessage}
              </div>
            )}
            {error && (
              <div
                data-testid="login-error"
                className="rounded-sm border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100"
              >
                <p className="font-medium">{t("auth.error_title")}</p>
                <p className="mt-1 text-red-100/80">{error}</p>
              </div>
            )}
            <button
              data-testid="login-submit-btn"
              disabled={isBusy}
              className="btn-volt w-full py-3 text-xs disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t("auth.signing_in") : t("auth.signin_eyebrow")}
            </button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            {t("auth.no_account")}{" "}
            <Link to={registerLink} className="text-volt underline-offset-4 hover:underline" data-testid="register-link">
              {t("auth.create_one")}
            </Link>
          </p>

          <div className="mt-4 rounded-sm border border-dashed border-border bg-card/40 p-4 text-sm text-muted-foreground">
            <div className="label-eyebrow text-volt mb-2">{t("auth.host_cta_eyebrow")}</div>
            <p>
              {t("auth.host_cta_body")}{" "}
              <Link to={hostRegisterLink} className="text-volt underline-offset-4 hover:underline">
                {t("auth.host_cta_action")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
